import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2, Film, Loader2, Download } from "lucide-react";
import { api, errMsg, fmtBytes } from "@/lib/api";

const MAX = 100 * 1024 * 1024;

const Thumb = ({ post, m, onDelete }) => {
  const [url, setUrl] = useState(null);
  const isImg = m.content_type?.startsWith("image/");
  useEffect(() => {
    if (!isImg) return;
    let u;
    api.get(`/posts/${post.id}/media/${m.id}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [post.id, m.id, isImg]);

  const download = async () => {
    try {
      const r = await api.get(`/posts/${post.id}/media/${m.id}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = m.nome; a.click();
    } catch { toast.error("Download non riuscito"); }
  };

  return (
    <div className="group relative aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200" data-testid={`post-media-${m.id}`}>
      {isImg ? (url ? <img src={url} alt={m.nome} className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>)
        : <div className="h-full w-full flex flex-col items-center justify-center text-[#0A1128] p-2"><Film className="h-7 w-7" /><span className="text-[10px] font-semibold text-center truncate w-full mt-1">{m.nome}</span></div>}
      <div className="absolute inset-0 bg-[#0A1128]/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 text-white">
        <span className="text-[10px] font-semibold">{fmtBytes(m.size)}</span>
        <div className="flex gap-1">
          <button type="button" onClick={download} className="p-1.5 rounded-lg bg-white/15 hover:bg-white/30" data-testid={`post-media-download-${m.id}`}><Download className="h-4 w-4" /></button>
          <button type="button" onClick={() => onDelete(m)} className="p-1.5 rounded-lg bg-white/15 hover:bg-[#E8173A]" data-testid={`post-media-delete-${m.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

export const PostMedia = ({ post, onChange }) => {
  const [uploading, setUploading] = useState(null);
  const ref = useRef();

  const upload = async (files) => {
    let current = post;
    for (const f of files) {
      if (f.size > MAX) { toast.error(`«${f.name}» supera la dimensione massima di 100 MB`); continue; }
      const fd = new FormData(); fd.append("file", f);
      setUploading(f.name);
      try { current = (await api.post(`/posts/${post.id}/media`, fd)).data; onChange(current); }
      catch (e) { toast.error(errMsg(e, "Caricamento non riuscito")); }
    }
    setUploading(null);
  };

  const remove = async (m) => {
    try { onChange((await api.delete(`/posts/${post.id}/media/${m.id}`)).data); toast.success("File rimosso"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="post-media-section">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Immagini e video</div>
      <div className="grid grid-cols-4 gap-2">
        {post.media.map((m) => <Thumb key={m.id} post={post} m={m} onDelete={remove} />)}
        <button type="button" onClick={() => !uploading && ref.current.click()} data-testid="post-media-add"
          className="aspect-square rounded-xl border-2 border-dashed border-[#0A1128]/25 hover:border-[#E8173A] text-[#0A1128] flex flex-col items-center justify-center gap-1 transition-colors">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
          <span className="text-[10px] font-bold">{uploading ? "Caricamento..." : "Aggiungi"}</span>
        </button>
      </div>
      <input ref={ref} type="file" multiple accept="image/*,video/*" className="hidden" data-testid="post-media-input"
        onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} />
      <div className="text-[11px] text-slate-400 mt-1.5">JPG, PNG, WEBP, GIF, HEIC, MP4, MOV, WEBM · max 100 MB</div>
    </div>
  );
};
